// Diff card rendered inline in the chat when the graph_builder agent
// stages a proposal. Mirrors the design-doc spec (§7.3) but quieter:
// editorial cream/slate, no liquid-glass.
//
// Supports the five non-type proposal kinds:
//   - create:        single new node
//   - create_batch:  many new nodes from one source (per-row select)
//   - update:        version bump of an existing node
//   - delete:        soft-delete an existing node
//   - link / unlink: edge between two existing nodes
//
// new_node_type proposals render via GraphTypeProposalCard — distinct
// visual treatment because the cost of approving is higher (schema
// extends for the brand).

import {
  AlertCircle,
  ArrowRight,
  Check,
  Circle,
  FileText,
  Image as ImageIcon,
  Layers,
  Link2,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  Quote,
  ShieldX,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { GraphProposal } from "@/lib/types";
import { cn } from "@/lib/utils";

interface Props {
  proposal: GraphProposal;
  onApply: (selectedIndices?: number[]) => Promise<void> | void;
  onReject: (userReason?: string) => Promise<void> | void;
  /** When set, the card disables its buttons and shows the result inline.
   * Set by the parent when the proposal has been applied or rejected. */
  resolved?: { kind: "applied"; affected: number } | { kind: "rejected"; reason: string };
}

export function GraphProposalCard({ proposal, onApply, onReject, resolved }: Props) {
  const [busy, setBusy] = useState<"apply" | "reject" | null>(null);
  const [selected, setSelected] = useState<Set<number>>(() => {
    const items = (proposal.payload as { items?: unknown[] }).items;
    return new Set(
      Array.isArray(items) ? items.map((_, i) => i) : []
    );
  });
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const kindMeta = kindMetadata(proposal);

  const handleApply = async (subset?: number[]) => {
    setBusy("apply");
    try {
      await onApply(subset);
    } finally {
      setBusy(null);
    }
  };
  const handleReject = async () => {
    setBusy("reject");
    try {
      await onReject(rejectReason.trim() || undefined);
    } finally {
      setBusy(null);
      setShowRejectInput(false);
    }
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card/80 overflow-hidden",
        "shadow-[0_1px_0_rgba(0,0,0,0.02)]",
        // Quiet slate accent rail per design doc §7.0
        "relative pl-3"
      )}
    >
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-slate-400/70"
      />
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-start gap-3">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
          <kindMeta.Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground">
            Proposed graph change
          </div>
          <div className="text-sm font-medium text-foreground mt-0.5">
            {kindMeta.title}
          </div>
        </div>
        {resolved && (
          <ResolvedBadge kind={resolved.kind} />
        )}
      </div>

      {/* Reason */}
      {proposal.reason && (
        <div className="px-4 pb-2 text-sm text-muted-foreground italic">
          <Quote className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5 opacity-50" />
          {proposal.reason}
        </div>
      )}

      {/* Body — varies by kind */}
      <div className="px-4 pb-3">
        <ProposalBody
          proposal={proposal}
          selected={selected}
          onToggle={(i) =>
            setSelected((prev) => {
              const next = new Set(prev);
              if (next.has(i)) next.delete(i);
              else next.add(i);
              return next;
            })
          }
          disabled={busy !== null || !!resolved}
        />
      </div>

      {/* Provenance pill */}
      <ProvenancePill proposal={proposal} />

      {/* Actions */}
      {!resolved && (
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center gap-2 flex-wrap">
          {proposal.kind === "create_batch" ? (
            <>
              <Button
                size="sm"
                onClick={() => handleApply()}
                disabled={busy !== null}
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {busy === "apply" ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                )}
                Apply all
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  handleApply(
                    Array.from(selected).sort((a, b) => a - b)
                  )
                }
                disabled={busy !== null || selected.size === 0}
              >
                Apply selected ({selected.size})
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => handleApply()}
              disabled={busy !== null}
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {busy === "apply" ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5 mr-1.5" />
              )}
              Apply
            </Button>
          )}
          {showRejectInput ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Why reject? (optional, helps the agent)"
                className="flex-1 text-sm px-2 py-1 rounded-md border border-border bg-background outline-none focus:ring-2 focus:ring-slate-300"
                autoFocus
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handleReject}
                disabled={busy !== null}
              >
                {busy === "reject" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  "Confirm"
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowRejectInput(false);
                  setRejectReason("");
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowRejectInput(true)}
              disabled={busy !== null}
              className="text-muted-foreground"
            >
              <ShieldX className="h-3.5 w-3.5 mr-1.5" />
              Reject
            </Button>
          )}
        </div>
      )}

      {resolved && (
        <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          {resolved.kind === "applied"
            ? `Applied — ${resolved.affected} node${resolved.affected === 1 ? "" : "s"} updated.`
            : resolved.reason
              ? `Rejected — "${resolved.reason}".`
              : "Rejected."}
        </div>
      )}
    </div>
  );
}

function ResolvedBadge({ kind }: { kind: "applied" | "rejected" }) {
  if (kind === "applied") {
    return (
      <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
        Applied
      </div>
    );
  }
  return (
    <div className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
      Rejected
    </div>
  );
}

function kindMetadata(p: GraphProposal): {
  title: string;
  Icon: typeof Plus;
} {
  switch (p.kind) {
    case "create":
      return {
        title: `Add ${pickCategory(p) ?? "node"}`,
        Icon: Plus,
      };
    case "create_batch": {
      const items = (p.payload as { items?: unknown[] }).items;
      const count = Array.isArray(items) ? items.length : 0;
      const cat =
        Array.isArray(items) && items.length > 0
          ? (items[0] as { category?: string }).category ?? "node"
          : "node";
      return {
        title: `Add ${count} ${cat}${count === 1 ? "" : "s"}`,
        Icon: Layers,
      };
    }
    case "update":
      return { title: "Update node", Icon: Pencil };
    case "delete":
      return { title: "Delete node", Icon: Trash2 };
    case "link":
      return { title: "Link two nodes", Icon: Link2 };
    case "unlink":
      return { title: "Unlink two nodes", Icon: Link2 };
    case "reparent":
      return { title: "Move node", Icon: ArrowRight };
    default:
      return { title: "Graph change", Icon: AlertCircle };
  }
}

function pickCategory(p: GraphProposal): string | undefined {
  if (p.kind === "create") return (p.payload as { category?: string }).category;
  return undefined;
}

// ─── Body renderers per kind ──────────────────────────────────────────

function ProposalBody({
  proposal,
  selected,
  onToggle,
  disabled,
}: {
  proposal: GraphProposal;
  selected: Set<number>;
  onToggle: (i: number) => void;
  disabled: boolean;
}) {
  const p = proposal.payload;

  if (proposal.kind === "create") {
    return <NodeRow content={(p as { content?: string }).content || ""} />;
  }

  if (proposal.kind === "create_batch") {
    const items = (p as { items?: Array<{ content?: string; category?: string }> }).items ?? [];
    return (
      <div className="flex flex-col gap-1">
        {items.map((item, i) => {
          const isSelected = selected.has(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onToggle(i)}
              disabled={disabled}
              className={cn(
                "flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors w-full",
                "hover:bg-accent/50 disabled:cursor-default disabled:hover:bg-transparent"
              )}
            >
              <div
                className={cn(
                  "h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0",
                  isSelected
                    ? "bg-slate-900 border-slate-900"
                    : "bg-card border-border"
                )}
              >
                {isSelected && <Check className="h-3 w-3 text-white" />}
              </div>
              <div className="text-sm text-foreground flex-1 truncate">
                {item.content || <span className="text-muted-foreground italic">(empty)</span>}
              </div>
              {item.category && (
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground/80 shrink-0">
                  {item.category}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  if (proposal.kind === "update") {
    const u = p as { target_node_id?: string; content?: string; display_label?: string };
    return (
      <div className="space-y-1.5">
        <div className="text-xs text-muted-foreground">
          Target: <code className="text-[11px] bg-muted px-1 py-0.5 rounded">{u.target_node_id}</code>
        </div>
        {u.content && (
          <div className="text-sm text-foreground bg-amber-50 border-l-2 border-amber-300 pl-2 py-1.5">
            <span className="text-[10px] uppercase tracking-wide text-amber-700 mr-2">new</span>
            {u.content}
          </div>
        )}
      </div>
    );
  }

  if (proposal.kind === "delete") {
    const d = p as { target_node_id?: string };
    return (
      <div className="text-xs text-muted-foreground">
        Soft-delete{" "}
        <code className="text-[11px] bg-muted px-1 py-0.5 rounded">
          {d.target_node_id}
        </code>{" "}
        — audit log retained, node hidden from live reads.
      </div>
    );
  }

  if (proposal.kind === "link" || proposal.kind === "unlink") {
    const l = p as { from_id?: string; to_id?: string; relationship?: string };
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <code className="text-[11px] bg-muted px-1 py-0.5 rounded">{l.from_id}</code>
        <ArrowRight className="h-3 w-3" />
        <span className="font-medium">:{l.relationship}</span>
        <ArrowRight className="h-3 w-3" />
        <code className="text-[11px] bg-muted px-1 py-0.5 rounded">{l.to_id}</code>
      </div>
    );
  }

  if (proposal.kind === "reparent") {
    const r = p as {
      target_node_id?: string;
      new_parent_id?: string;
      new_relationship?: string;
      target_snapshot?: { display_label?: string; category?: string };
      new_parent_snapshot?: { display_label?: string; category?: string };
    };
    const targetLabel =
      r.target_snapshot?.display_label || r.target_node_id || "node";
    const parentLabel =
      r.new_parent_snapshot?.display_label || r.new_parent_id || "new parent";
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900">
            {targetLabel}
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            now child of
          </span>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900">
            {parentLabel}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          New relationship: <code className="bg-muted px-1 rounded">:{r.new_relationship || "HAS_CHILD"}</code>
          {r.target_snapshot?.category && (
            <span className="ml-2 text-muted-foreground/70">
              · {r.target_snapshot.category}
            </span>
          )}
        </div>
      </div>
    );
  }

  return null;
}

function NodeRow({ content }: { content: string }) {
  return (
    <div className="flex items-start gap-2.5 px-2 py-1.5 bg-accent/40 rounded-md">
      <Circle className="h-2 w-2 fill-slate-700 text-slate-700 mt-1.5 shrink-0" />
      <div className="text-sm text-foreground">{content}</div>
    </div>
  );
}

// ─── Provenance pill ──────────────────────────────────────────────────

function ProvenancePill({ proposal }: { proposal: GraphProposal }) {
  const p = (proposal.provenance || {}) as {
    source_kind?: string;
    source_ref?: string;
    source_locator?: string;
    source_confidence?: number;
  };
  const kind = p.source_kind;
  if (!kind) return null;

  const Icon = sourceKindIcon(kind);
  const label = sourceLabel(p);
  const conf = p.source_confidence;

  return (
    <div className="px-4 pb-2 flex items-center gap-2 text-[11px] text-muted-foreground">
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/60 border border-border">
        <Icon className="h-3 w-3" />
        <span className="truncate max-w-[280px]">{label}</span>
      </div>
      {typeof conf === "number" && conf < 0.7 && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700">
          <AlertCircle className="h-3 w-3" />
          low confidence ({conf.toFixed(2)})
        </div>
      )}
    </div>
  );
}

function sourceKindIcon(kind: string): typeof FileText {
  switch (kind) {
    case "pdf":
      return FileText;
    case "image":
      return ImageIcon;
    case "url":
      return Link2;
    case "spreadsheet":
      return Layers;
    case "chat":
    default:
      return MessageSquare;
  }
}

function sourceLabel(p: {
  source_kind?: string;
  source_ref?: string;
  source_locator?: string;
}): string {
  if (!p.source_ref) return p.source_kind ?? "source";
  const ref = p.source_ref.length > 60 ? p.source_ref.slice(0, 57) + "…" : p.source_ref;
  if (p.source_locator) return `${ref} · ${p.source_locator}`;
  return ref;
}
